/**
 * This file is to simulate the old school window.confirm but wrap everything
 * within a promise and nice UI. To use this promise,
 * simply: const result = await Confirm({body: 'Are you sure?'});
 */
import React, { ReactElement, useCallback, useState } from 'react';
import { createRoot } from 'react-dom/client';
import Button from '@atlaskit/button/standard-button';
import Modal, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalTransition,
} from '@atlaskit/modal-dialog';

let confirmPromiseResolve: Function;
let confirmModal: HTMLDivElement | null;

type ConfirmProps = {
  title?: string;
  body: string | ReactElement;
};

const ConfirmModal: React.FC<ConfirmProps> = ({ title = 'Confirmation', body }) => {
  const [isOpen, setIsOpen] = useState(true);
  const handleCancelClick = useCallback(() => {
    setIsOpen(false);
    confirmPromiseResolve(false);
    if (confirmModal) {
      confirmModal.remove();
      confirmModal = null;
    }
  }, []);
  const handleConfirmClick = useCallback(() => {
    setIsOpen(false);
    confirmPromiseResolve(true);
    if (confirmModal) {
      confirmModal.remove();
      confirmModal = null;
    }
  }, []);

  if (!isOpen) return <></>;

  return (
    <div>
      <ModalTransition>
        <Modal onClose={handleCancelClick}>
          <ModalHeader>
            <ModalTitle>{title}</ModalTitle>
          </ModalHeader>
          <ModalBody>{body}</ModalBody>
          <ModalFooter>
            <Button appearance="subtle" onClick={handleCancelClick}>
              Cancel
            </Button>
            <Button appearance="primary" onClick={handleConfirmClick} autoFocus>
              Confirm
            </Button>
          </ModalFooter>
        </Modal>
      </ModalTransition>
    </div>
  );
};

export const Confirm = async ({ title, body }: ConfirmProps): Promise<boolean> => {
  if (confirmModal) {
    confirmModal.remove();
  } else {
    confirmModal = document.createElement('div');
  }
  if (confirmPromiseResolve) confirmPromiseResolve(false);

  document.body.appendChild(confirmModal);

  const confirmModalRoot = createRoot(confirmModal!);
  confirmModalRoot.render(<ConfirmModal title={title} body={body} />);

  return new Promise((res: Function) => {
    confirmPromiseResolve = res;
  });
};
